import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { X, ExternalLink, Settings } from 'lucide-react';
/* =========================================================================
   TYPES
   ========================================================================= */
type AgentId = 'discovery' | 'extraction' | 'validation' | 'underwriting' | 'audit';
type AgentSummary = {
  id: AgentId;
  name: string;
  ips: number;
  processed: number;
  confidence?: number;
};
type AgentDetail = {
  id: 'discovery';
  sources: string[];
  query: string;
  indexed: number;
} | {
  id: 'extraction';
  fieldParity: number;
  duplicatesResolved: number;
  schemaVersion: string;
} | {
  id: 'validation';
  evidence: {
    erp: boolean;
    buyer: boolean;
    ship: boolean;
    bank: boolean;
    hash: boolean;
  };
  buyerMismatch: number;
  erpFail: number;
} | {
  id: 'underwriting';
  scoreFormula: string;
  threshold: number;
  above085: number;
} | {
  id: 'audit';
  verifications24h: number;
  fingerprint: string;
  explorerUrl: string;
};
/* =========================================================================
   SEED DATA
   ========================================================================= */
const SEED: AgentSummary[] = [{
  id: 'discovery',
  name: 'Discovery Agent',
  ips: 854,
  processed: 1057000
}, {
  id: 'extraction',
  name: 'Extraction Agent',
  ips: 228,
  processed: 47481,
  confidence: 0.785
}, {
  id: 'validation',
  name: 'Validation Agent',
  ips: 191,
  processed: 40140,
  confidence: 0.893
}, {
  id: 'underwriting',
  name: 'Underwriting Agent',
  ips: 745,
  processed: 83840,
  confidence: 0.965
}, {
  id: 'audit',
  name: 'On-Chain Audit Agent',
  ips: 80,
  processed: 6842
}];
const DETAILS: Record<AgentId, AgentDetail> = {
  discovery: {
    id: 'discovery',
    sources: ['QBO', 'NetSuite', 'SAP'],
    query: 'customer.creditLimit > 10000',
    indexed: 1059421
  },
  extraction: {
    id: 'extraction',
    fieldParity: 0.785,
    duplicatesResolved: 3200,
    schemaVersion: 'v1.8'
  },
  validation: {
    id: 'validation',
    evidence: {
      erp: true,
      buyer: true,
      ship: true,
      bank: false,
      hash: true
    },
    buyerMismatch: 89,
    erpFail: 45
  },
  underwriting: {
    id: 'underwriting',
    scoreFormula: '0.39*extract + 0.25*buyer + 0.15*hist + 0.10*ship + 0.10*fingerprint',
    threshold: 0.85,
    above085: 73200
  },
  audit: {
    id: 'audit',
    verifications24h: 312,
    fingerprint: 'SHA256: 16d7e3…',
    explorerUrl: 'https://sepolia.etherscan.io/tx/0x88df016429689c079f3b2f6ad39fa052532c56795b733da78a91ebe6a713944b'
  }
};
/* =========================================================================
   RANDOMIZED ACTIVITY GENERATOR
   ========================================================================= */
const generateRecentActivity = (agentType: AgentId) => {
  const baseInvoices = [{
    prefix: 'INV-',
    base: 10231
  }, {
    prefix: 'INV-',
    base: 42751
  }, {
    prefix: 'INV-',
    base: 38492
  }, {
    prefix: 'INV-',
    base: 91823
  }, {
    prefix: 'INV-',
    base: 55617
  }, {
    prefix: 'INV-',
    base: 72384
  }, {
    prefix: 'INV-',
    base: 14926
  }, {
    prefix: 'INV-',
    base: 88531
  }, {
    prefix: 'INV-',
    base: 26749
  }, {
    prefix: 'INV-',
    base: 63218
  }];
  const statuses = {
    discovery: ['Discovered', 'Indexed', 'Qualified', 'Skipped', 'Queued'],
    extraction: ['Extracted', 'Parsed', 'Field Error', 'Validated', 'Retry'],
    validation: ['Validated', 'Failed AML', 'Passed', 'Flagged', 'Pending'],
    underwriting: ['Scored: 0.92', 'Scored: 0.71', 'Below Threshold', 'Approved', 'Manual Review'],
    audit: ['On-chain', 'Verified', 'Hash: 0x3f2...', 'Settled', 'Pending']
  };
  // Shuffle and map
  const shuffled = [...baseInvoices].sort(() => Math.random() - 0.5);
  const agentStatuses = statuses[agentType];
  return shuffled.map((inv, i) => ({
    id: `${inv.prefix}${inv.base + Math.floor(Math.random() * 1000)}`,
    action: agentStatuses[Math.floor(Math.random() * agentStatuses.length)]
  }));
};
/* =========================================================================
   PAGE
   ========================================================================= */
export function Agents() {
  const [list, setList] = useState<AgentSummary[]>(SEED);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<AgentSummary | null>(null);
  // sparkline buffers per agent id
  const lines = useRef<Record<string, number[]>>({});
  useEffect(() => {
    SEED.forEach(a => {
      if (!lines.current[a.id]) lines.current[a.id] = Array.from({
        length: 24
      }, () => a.ips);
    });
  }, []);
  // light live jitter for demo realism
  useEffect(() => {
    const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
    const t = setInterval(() => {
      setList(prev => prev.map(a => {
        const maxIps = a.id === 'underwriting' ? 900 : 300;
        const ips = clamp(a.ips + Math.floor(Math.random() * 15 - 7), 40, maxIps);
        const processed = a.processed + Math.floor(ips * (1 + Math.random()));
        const confidence = a.confidence != null ? clamp(a.confidence + (Math.random() * 0.01 - 0.005), 0.6, 0.99) : undefined;
        const buf = lines.current[a.id]!;
        buf.push(ips);
        if (buf.length > 24) buf.shift();
        return {
          ...a,
          ips,
          processed,
          confidence
        };
      }));
    }, 2500);
    return () => clearInterval(t);
  }, []);
  const topRowAgents = list.slice(0, 2);
  const bottomRowAgents = list.slice(2, 5);
  return <div className="w-full" data-id="element-380">
      <header className="mb-8" data-id="element-381">
        <h1 className="text-4xl font-bold text-gray-900" data-id="element-382">Agents</h1>
      </header>
      <div className="w-full space-y-5" data-id="element-383">
        {/* Top row: 2 agents */}
        <div className="grid grid-cols-2 gap-5" data-id="element-384">
          {topRowAgents.map(a => <AgentCard key={a.id} summary={a} sparkline={lines.current[a.id] || []} onOpen={() => {
          setActive(a);
          setOpen(true);
        }} data-id="element-385" />)}
        </div>
        {/* Bottom row: 3 agents */}
        <div className="grid grid-cols-3 gap-5" data-id="element-386">
          {bottomRowAgents.map(a => <AgentCard key={a.id} summary={a} sparkline={lines.current[a.id] || []} onOpen={() => {
          setActive(a);
          setOpen(true);
        }} data-id="element-387" />)}
        </div>
      </div>
      <AgentDrawer open={open} onClose={() => setOpen(false)} summary={active || undefined} data-id="element-388" />
    </div>;
}
/* =========================================================================
   CARD
   ========================================================================= */
function AgentCard({
  summary,
  sparkline,
  onOpen
}: {
  summary: AgentSummary;
  sparkline: number[];
  onOpen: () => void;
}) {
  const fmt = (n: number) => n.toLocaleString();
  return <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-gray-400 transition-all cursor-pointer" onClick={onOpen} title="Open details" role="button" data-id="element-389">
      <div className="text-lg font-bold mb-5 text-gray-800" data-id="element-390">{summary.name}</div>
      <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-391">
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-392">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-393">
            IPS
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-394">
            {summary.ips}/sec
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-395">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-396">
            Processed
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-397">
            {fmt(summary.processed)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-398">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-399">
            Confidence
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-400">
            {summary.confidence != null ? `${(summary.confidence * 100).toFixed(1)}%` : '—'}
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-401">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-402">
            Status
          </div>
          <div className="text-sm font-semibold text-green-600" data-id="element-403">Running</div>
        </div>
      </div>
      <Sparkline points={sparkline} data-id="element-404" />
    </div>;
}
function Sparkline({
  points
}: {
  points: number[];
}) {
  if (!points.length) return <div className="h-8 bg-gray-50 rounded" data-id="element-405" />;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const norm = points.map((p, i) => {
    const x = i / Math.max(1, points.length - 1) * 100;
    const y = 100 - (p - min) / Math.max(1, max - min) * 100;
    return `${x},${y}`;
  });
  return <svg viewBox="0 0 100 30" className="h-8 w-full text-gray-400" data-id="element-406">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={norm.map(p => p.replace(',', ' ')).join(' ')} data-id="element-407" />
    </svg>;
}
/* =========================================================================
   DRAWER
   ========================================================================= */
function AgentDrawer({
  open,
  onClose,
  summary
}: {
  open: boolean;
  onClose: () => void;
  summary?: AgentSummary;
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [recentActivity, setRecentActivity] = useState<Array<{
    id: string;
    action: string;
  }>>([]);
  // Generate new randomized activity when drawer opens or agent changes
  useEffect(() => {
    if (open && summary) {
      setRecentActivity(generateRecentActivity(summary.id));
    }
  }, [open, summary]);
  if (!open || !summary) return null;
  const d = DETAILS[summary.id];
  const fmt = (n: number) => n.toLocaleString();
  const handleDownloadLogs = () => {
    // Generate sample log data
    const logs = [];
    const now = new Date();
    // Add header row
    logs.push(['Timestamp', 'Invoice ID', 'Action', 'Status', 'Processing Time (ms)', 'Confidence']);
    // Generate 100 sample log entries
    for (let i = 0; i < 100; i++) {
      const timestamp = new Date(now.getTime() - i * 60000).toISOString();
      const invoiceId = `INV-${58000 + i}`;
      const actions = ['Discovered', 'Extracted', 'Validated', 'Underwritten', 'Tokenized'];
      const action = actions[Math.floor(Math.random() * actions.length)];
      const status = Math.random() > 0.95 ? 'Error' : 'Success';
      const processingTime = Math.floor(Math.random() * 500) + 50;
      const confidence = summary.confidence ? (summary.confidence * 100 + (Math.random() * 10 - 5)).toFixed(1) + '%' : 'N/A';
      logs.push([timestamp, invoiceId, action, status, processingTime.toString(), confidence]);
    }
    // Convert to CSV
    const csvContent = logs.map(row => row.join(',')).join('\n');
    // Create download
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${summary.name.toLowerCase().replace(' ', '_')}_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <>
      <div className="fixed inset-0 z-50" data-id="element-408">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-409" />
        <aside className="absolute right-0 top-0 h-full w-[480px] bg-white shadow-xl overflow-y-auto" data-id="element-410">
          <div className="flex items-center justify-between p-5 border-b border-gray-200" data-id="element-411">
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-700" data-id="element-412">
              Agent Detail
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-xl p-1" data-id="element-413">
              <X size={20} data-id="element-414" />
            </button>
          </div>
          {/* Core Metrics */}
          <Section title="Core Metrics" data-id="element-415">
            <div className="grid grid-cols-2 gap-3" data-id="element-416">
              <MetricBox label="Processed" value={fmt(summary.processed)} data-id="element-417" />
              <MetricBox label="Throughput" value={`${summary.ips}/sec`} data-id="element-418" />
              <MetricBox label="Confidence" value={summary.confidence != null ? `${(summary.confidence * 100).toFixed(1)}%` : '—'} data-id="element-419" />
              <MetricBox label="Status" value="Running" data-id="element-420" />
            </div>
          </Section>
          {/* Agent-specific content */}
          {renderSpecific(d)}
          {/* Recent Activity */}
          <Section title="Recent Activity" data-id="element-421">
            <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-422">
              {recentActivity.map((activity, i) => <div key={i} className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 last:border-b-0 text-sm" data-id="element-423">
                  <span className="font-mono text-gray-700" data-id="element-424">{activity.id}</span>
                  <span className="text-gray-600" data-id="element-425">{activity.action}</span>
                </div>)}
            </div>
          </Section>
          {/* Action Buttons */}
          <div className="flex gap-2 p-5" data-id="element-426">
            <button onClick={handleDownloadLogs} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-427">
              Download Logs
            </button>
            <button onClick={() => setShowSettings(true)} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-428">
              Adjust Settings
            </button>
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800" data-id="element-429">
              Close
            </button>
          </div>
        </aside>
      </div>
      {/* Settings Modal */}
      {showSettings && <AgentSettingsModal agent={summary} onClose={() => setShowSettings(false)} data-id="element-430" />}
    </>;
}
/* =========================================================================
   SETTINGS MODAL
   ========================================================================= */
function AgentSettingsModal({
  agent,
  onClose
}: {
  agent: AgentSummary;
  onClose: () => void;
}) {
  const [alertThreshold, setAlertThreshold] = useState(90);
  const [maxThroughput, setMaxThroughput] = useState(1000);
  const [autoRetry, setAutoRetry] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const handleSave = () => {
    // In a real app, this would save settings to backend
    alert('Settings saved successfully!');
    onClose();
  };
  return <div className="fixed inset-0 z-[60] flex items-center justify-center" data-id="element-431">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-432" />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-433">
        <div className="flex items-center justify-between mb-4" data-id="element-434">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-435">
            {agent.name} Settings
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-436">
            <X size={20} data-id="element-437" />
          </button>
        </div>
        <div className="space-y-4" data-id="element-438">
          {/* Alert Threshold */}
          <div data-id="element-439">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-440">
              Alert Threshold (%)
            </label>
            <input type="range" min="0" max="100" value={alertThreshold} onChange={e => setAlertThreshold(Number(e.target.value))} className="w-full" data-id="element-441" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-442">
              <span data-id="element-443">0%</span>
              <span className="font-medium text-gray-900" data-id="element-444">
                {alertThreshold}%
              </span>
              <span data-id="element-445">100%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-446">
              Alert when confidence drops below this threshold
            </p>
          </div>
          {/* Max Throughput */}
          <div data-id="element-447">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-448">
              Max Throughput (per second)
            </label>
            <input type="number" value={maxThroughput} onChange={e => setMaxThroughput(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900" data-id="element-449" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-450">
              Maximum processing rate to prevent overload
            </p>
          </div>
          {/* Auto Retry */}
          <div className="flex items-center justify-between" data-id="element-451">
            <div data-id="element-452">
              <label className="text-sm font-medium text-gray-700" data-id="element-453">
                Auto Retry Failed Items
              </label>
              <p className="text-xs text-gray-500" data-id="element-454">
                Automatically retry failed processing attempts
              </p>
            </div>
            <button onClick={() => setAutoRetry(!autoRetry)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoRetry ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-455">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoRetry ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-456" />
            </button>
          </div>
          {/* Notifications */}
          <div className="flex items-center justify-between" data-id="element-457">
            <div data-id="element-458">
              <label className="text-sm font-medium text-gray-700" data-id="element-459">
                Enable Notifications
              </label>
              <p className="text-xs text-gray-500" data-id="element-460">
                Receive alerts for errors and warnings
              </p>
            </div>
            <button onClick={() => setNotificationsEnabled(!notificationsEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationsEnabled ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-461">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-462" />
            </button>
          </div>
        </div>
        <div className="flex gap-3 mt-6" data-id="element-463">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-464">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800" data-id="element-465">
            Save Settings
          </button>
        </div>
      </div>
    </div>;
}
/* agent-specific panels */
function renderSpecific(d: AgentDetail) {
  switch (d.id) {
    case 'discovery':
      return <Section title="Discovery Configuration" data-id="element-466">
          <div className="grid grid-cols-2 gap-3" data-id="element-467">
            <div className="col-span-2 bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-468">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-469">
                Sources
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-470">
                {d.sources.join(' • ')}
              </div>
            </div>
            <div className="col-span-2 bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-471">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-472">
                Query
              </div>
              <div className="text-xs font-mono text-gray-900" data-id="element-473">{d.query}</div>
            </div>
            <MetricBox label="Indexed" value={d.indexed.toLocaleString()} data-id="element-474" />
            <MetricBox label="Extraction Rate" value="1% (Target)" data-id="element-475" />
          </div>
        </Section>;
    case 'extraction':
      return <Section title="Extraction Quality" data-id="element-476">
          <div className="grid grid-cols-2 gap-3" data-id="element-477">
            <MetricBox label="Field Parity" value={`${(d.fieldParity * 100).toFixed(1)}%`} data-id="element-478" />
            <MetricBox label="Duplicates Resolved" value={d.duplicatesResolved.toLocaleString()} data-id="element-479" />
            <MetricBox label="Schema Version" value={d.schemaVersion} data-id="element-480" />
            <MetricBox label="Cost per Invoice" value="$0.0055" data-id="element-481" />
          </div>
        </Section>;
    case 'validation':
      return <Section title="Evidence Checks" data-id="element-482">
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-483">
            <EvidencePill label="ERP" pass={d.evidence.erp} data-id="element-484" />
            <EvidencePill label="BUYER" pass={d.evidence.buyer} data-id="element-485" />
            <EvidencePill label="SHIPPING" pass={d.evidence.ship} data-id="element-486" />
            <EvidencePill label="BANK" pass={d.evidence.bank} data-id="element-487" />
            <EvidencePill label="HASH" pass={d.evidence.hash} data-id="element-488" />
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-489">
            <MetricBox label="Buyer Mismatch" value={String(d.buyerMismatch)} data-id="element-490" />
            <MetricBox label="ERP Fails" value={String(d.erpFail)} data-id="element-491" />
          </div>
        </Section>;
    case 'underwriting':
      return <Section title="Underwriting Model" data-id="element-492">
          <div className="bg-gray-50 border border-gray-200 p-3 rounded-md mb-4" data-id="element-493">
            <div className="text-[10px] uppercase text-gray-500 mb-2" data-id="element-494">
              Score Formula
            </div>
            <div className="text-xs font-mono text-gray-700 leading-relaxed" data-id="element-495">
              {d.scoreFormula}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-496">
            <MetricBox label="Threshold" value={String(d.threshold)} data-id="element-497" />
            <MetricBox label="Above Threshold" value={d.above085.toLocaleString()} data-id="element-498" />
          </div>
        </Section>;
    case 'audit':
      return <Section title="On-Chain Verification" data-id="element-499">
          <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-500">
            <MetricBox label="Verifications (24h)" value={String(d.verifications24h)} data-id="element-501" />
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-502">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-503">
                Fingerprint
              </div>
              <div className="text-xs font-mono text-gray-900" data-id="element-504">
                {d.fingerprint}
              </div>
            </div>
          </div>
          <a href={d.explorerUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" data-id="element-505">
            <ExternalLink size={14} data-id="element-506" />
            View on Etherscan
          </a>
        </Section>;
  }
}
/* primitives */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="p-5 border-b border-gray-100" data-id="element-507">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-4" data-id="element-508">
        {title}
      </div>
      {children}
    </section>;
}
function MetricBox({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-509">
      <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-510">{label}</div>
      <div className="text-sm font-semibold text-gray-900" data-id="element-511">{value}</div>
    </div>;
}
function EvidencePill({
  label,
  pass
}: {
  label: string;
  pass: boolean;
}) {
  return <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-medium border ${pass ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-512">
      {label} {pass ? 'PASS' : 'FAIL'}
    </span>;
}